import {
  INQUIRY_PROCESS_NAME,
  NEGOTIATION_PROCESS_NAME,
  PURCHASE_PROCESS_NAME,
  resolveLatestProcessName,
} from './transaction';
import { transitions as inquiryTransitions } from './transactionProcessInquiry';
import { transitions as negotiationTransitions } from './transactionProcessNegotiation';
import { transitions as purchaseTransitions } from './transactionProcessPurchase';

const RECEIVER_ROLE = 'customer';

// The platform polls Sharetribe every 60 seconds by default. A completion can
// need two polling passes before both its listing and transaction refs exist.
export const HANDOVER_SYNC_RETRY_DELAYS_MS = [2000, 8000, 20000, 40000, 70000];

export const getHandoverSyncRetryDelay = (error, retryAttempt) => {
  const status = error?.status;
  const isRetryable = status === 404 || status === 409 || status >= 500;
  return isRetryable ? HANDOVER_SYNC_RETRY_DELAYS_MS[retryAttempt] : undefined;
};

const purchaseCompletionTransitions = new Set([
  purchaseTransitions.MARK_RECEIVED_FROM_PURCHASED,
  purchaseTransitions.MARK_RECEIVED,
  purchaseTransitions.AUTO_MARK_RECEIVED,
  purchaseTransitions.MARK_RECEIVED_FROM_DISPUTED,
  purchaseTransitions.AUTO_COMPLETE,
]);

const receiverInitiatedPurchaseTransitions = new Set([
  purchaseTransitions.MARK_RECEIVED_FROM_PURCHASED,
  purchaseTransitions.MARK_RECEIVED,
]);

const negotiationCompletionTransitions = new Set([
  negotiationTransitions.ACCEPT_DELIVERABLE,
  negotiationTransitions.AUTO_ACCEPT_DELIVERABLE,
  negotiationTransitions.OPERATOR_ACCEPT_DELIVERABLE,
]);

const receiverInitiatedNegotiationTransitions = new Set([
  negotiationTransitions.ACCEPT_DELIVERABLE,
]);

const uuidValue = id => (typeof id === 'string' ? id : id?.uuid);

const observedTransitions = transaction => {
  const transitionHistory = transaction?.attributes?.transitions || [];
  return new Set(
    [
      transaction?.attributes?.lastTransition,
      ...transitionHistory.map(entry => entry?.transition),
    ].filter(Boolean)
  );
};

const donationRequest = transaction => {
  const listingId = uuidValue(transaction?.relationships?.listing?.data?.id);
  return listingId ? { sharetribeListingId: listingId } : null;
};

const transactionRequest = transaction => {
  const transactionId = uuidValue(transaction?.id);
  return transactionId ? { sharetribeTransactionId: transactionId } : null;
};

/**
 * Build the consumer handover request only after a successful receiver flow.
 * Transition history keeps this useful after reviews, while the endpoint's
 * idempotency makes reopening a completed transaction safe.
 */
export const handoverRequestForTransaction = ({ transaction, transactionRole }) => {
  if (!transaction || transactionRole !== RECEIVER_ROLE) {
    return null;
  }

  const processName = resolveLatestProcessName(transaction.attributes?.processName);
  const transitions = observedTransitions(transaction);

  if (
    processName === INQUIRY_PROCESS_NAME &&
    transitions.has(inquiryTransitions.CONFIRM_HANDOVER)
  ) {
    return donationRequest(transaction);
  }

  if (
    processName === PURCHASE_PROCESS_NAME &&
    [...purchaseCompletionTransitions].some(transition => transitions.has(transition))
  ) {
    return transactionRequest(transaction);
  }

  if (
    processName === NEGOTIATION_PROCESS_NAME &&
    [...negotiationCompletionTransitions].some(transition => transitions.has(transition))
  ) {
    return transactionRequest(transaction);
  }

  return null;
};

/** Build a request only for a receiver-initiated completion transition. */
export const handoverRequestForTransition = ({ transaction, transitionName }) => {
  if (!transaction || !transitionName) {
    return null;
  }

  const processName = resolveLatestProcessName(transaction.attributes?.processName);
  if (
    processName === INQUIRY_PROCESS_NAME &&
    transitionName === inquiryTransitions.CONFIRM_HANDOVER
  ) {
    return donationRequest(transaction);
  }

  if (
    processName === PURCHASE_PROCESS_NAME &&
    receiverInitiatedPurchaseTransitions.has(transitionName)
  ) {
    return transactionRequest(transaction);
  }

  if (
    processName === NEGOTIATION_PROCESS_NAME &&
    receiverInitiatedNegotiationTransitions.has(transitionName)
  ) {
    return transactionRequest(transaction);
  }

  return null;
};
